import i18n from '@/utils/i18n';
import React from 'react';
import { Analytics } from '@/utils/analytics';
import { APP_CONTAINER_ROUTES, ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import {
  AppContainerStackParams,
  OrderGuideStackParams,
} from '@/navigators/types';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, shadow, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, TouchableOpacity, View } from 'react-native';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';

interface Props {
  brandPageUUID: string;
  description: string;
  desktopImageURL: string;
  logoImageURL: string;
  mobileImageURL: string;
  name: string;
}

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.BrandPage>,
  StackNavigationProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.BrandPage>
>;

const DESKTOP_CARD_WIDTH = 408;
const DESKTOP_IMAGE_HEIGHT = 114;
const DESKTOP_IMAGE_WIDTH = 200;
const MOBILE_CARD_WIDTH = 160;
const MOBILE_IMAGE_HEIGHT = 48;
const MOBILE_IMAGE_WIDTH = 126;
const DESKTOP_LOGO_SIZE = 56;
const LOGO_SIZE = 48;

export const BrandCarouselItem: React.FC<Props> = ({
  brandPageUUID,
  description,
  desktopImageURL,
  mobileImageURL,
  logoImageURL,
  name,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { navigate } = useNavigation<Navigation>();
  const [styles] = useStyles();
  const {
    brandDescription,
    cardStyle,
    imageStyle,
    imageURL,
    logoImageContainerStyle,
  } = useSmallLayoutSizeValue(
    {
      brandDescription: null,
      cardStyle: styles.mobileCardStyle,
      imageStyle: styles.mobileImage,
      imageURL: mobileImageURL,
      logoImageContainerStyle: styles.logoImageContainerSmall,
    },
    {
      brandDescription: styles.desktopDescription,
      cardStyle: styles.desktopCardStyle,
      imageStyle: styles.desktopImage,
      imageURL: desktopImageURL,
      logoImageContainerStyle: styles.logoImageContainerLarge,
    },
  );

  const onOpenBrandPage = () => {
    Analytics.track('brand_page_carousel_card.press', {
      brandPageUUID,
    });
    navigate(
      isSmallLayout
        ? APP_CONTAINER_ROUTES.BrandPage
        : ORDER_GUIDE_ROUTES.BrandPage,
      {
        brandPageUUID,
      },
    );
  };

  return (
    <TouchableOpacity
      onPress={onOpenBrandPage}
      style={[styles.wrapper, cardStyle]}
      testID={isSmallLayout ? 'mobile-brand-card' : 'non-mobile-brand-card'}
    >
      <View collapsable={false}>
        <CachedImage
          alt=""
          contentFit="cover"
          source={{ uri: imageURL }}
          style={imageStyle}
          testID="brand-banner-image"
        />
        <View style={[styles.logoImageContainer, logoImageContainerStyle]}>
          <CachedImage
            alt={name}
            contentFit="contain"
            source={{ uri: logoImageURL }}
            style={styles.logoImage}
            testID="brand-logo-image"
          />
        </View>
      </View>
      <View style={styles.content}>
        <Text
          numberOfLines={1}
          style={[TYPOGRAPHY.listTitle, styles.brandName]}
        >
          {name}
        </Text>
        <Text
          numberOfLines={2}
          style={[TYPOGRAPHY.listSubtitle, brandDescription]}
        >
          {description}
        </Text>
        {!isSmallLayout ? (
          <View style={styles.seeAllButton}>
            <Text style={styles.seeAllText}>{i18n.t('see_all_items')}</Text>
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  brandName: {
    marginBottom: 4,
  },
  content: {
    flex: 1,
  },
  desktopCardStyle: {
    flexDirection: 'row',
    gap: 16,
    marginHorizontal: 8,
    marginTop: 8,
    width: DESKTOP_CARD_WIDTH,
  },
  desktopDescription: {
    color: theme.colors.secondary,
    flex: 1,
    marginBottom: 16,
  },
  desktopImage: {
    borderRadius: 8,
    height: DESKTOP_IMAGE_HEIGHT,
    width: DESKTOP_IMAGE_WIDTH,
  },
  logoImage: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    height: LOGO_SIZE,
    width: LOGO_SIZE,
  },
  logoImageContainer: {
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.08,
      radius: 24,
      x: 0,
      y: 12,
    }),
    borderRadius: 8,
    height: LOGO_SIZE,
    width: LOGO_SIZE,
  },
  logoImageContainerLarge: {
    backgroundColor: theme.colors.white,
    height: DESKTOP_LOGO_SIZE,
    left: 8,
    padding: 4,
    position: 'absolute',
    top: 8,
    width: DESKTOP_LOGO_SIZE,
  },
  logoImageContainerSmall: {
    marginBottom: 16,
    marginHorizontal: 'auto',
    marginTop: -24,
  },
  mobileCardStyle: {
    marginHorizontal: 6,
    marginTop: 4,
    width: MOBILE_CARD_WIDTH,
  },
  mobileImage: {
    borderRadius: 8,
    height: MOBILE_IMAGE_HEIGHT,
    width: MOBILE_IMAGE_WIDTH,
  },
  seeAllButton: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.accent,
    borderRadius: 20,
    height: 32,
    paddingHorizontal: 16,
  },
  seeAllText: {
    ...FONTS.primaryBold,
    color: theme.colors.white,
    fontSize: theme.fontSizes.small,
    lineHeight: 32,
  },
  wrapper: {
    ...shadow({
      color: theme.colors.black,
      elevation: 0,
      opacity: 0.05,
      radius: 12,
      x: 0,
      y: 6,
    }),
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.divider,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    marginBottom: 16,
    padding: 16,
  },
}));
